import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { FileUploader } from "@/components/FileUploader";
import { DatePicker } from "@/components/DatePicker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Send, Save, Loader2 } from "lucide-react";

const URGENCY = [
  { v: "rendah", l: "Rendah" },
  { v: "sedang", l: "Sedang" },
  { v: "tinggi", l: "Tinggi" },
  { v: "darurat", l: "Darurat" },
];

export default function ReportCreate() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = Boolean(id);

  const [vehicles, setVehicles] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [targetWorkshop, setTargetWorkshop] = useState("");
  const [manualWorkshop, setManualWorkshop] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    plate_number: "",
    vehicle_type: "",
    brand: "",
    year: "",
    phone: user?.phone || "",
    incident_date: "",
    location: "",
    damage_description: "",
    urgency: "sedang",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/vehicles").then(({ data }) => setVehicles(data)).catch(() => {});
    api.get("/workshops").then(({ data }) => setWorkshops(data)).catch(() => {});
    if (editing) {
      api.get(`/reports/${id}`).then(({ data }) => {
        setForm({
          plate_number: data.plate_number || "",
          vehicle_type: data.vehicle_type || "",
          brand: data.brand || "",
          year: data.year || "",
          phone: data.phone || "",
          incident_date: data.incident_date || "",
          location: data.location || "",
          damage_description: data.damage_description || "",
          urgency: data.urgency || "sedang",
        });
        if (data.preferred_workshop_id) {
          setTargetWorkshop(data.preferred_workshop_id);
        } else if (data.preferred_workshop_name) {
          setTargetWorkshop("__other__");
          setManualWorkshop(data.preferred_workshop_name);
        }
        // load damage files metadata
        if (data.damage_files?.length) {
          Promise.all(
            data.damage_files.map((fid) =>
              api.get("/reports/" + id).then(() => ({ id: fid, is_video: false }))
            )
          ).then(setFiles);
        }
      });
    }
    // eslint-disable-next-line
  }, [id]);

  const pickVehicle = (plate) => {
    const v = vehicles.find((x) => x.plate_number === plate);
    if (v) {
      setForm((f) => ({
        ...f,
        plate_number: v.plate_number,
        vehicle_type: v.type || "",
        brand: v.brand || "",
        year: v.year || "",
      }));
    }
  };

  const submit = async (doSubmit) => {
    if (!form.plate_number.trim()) return toast.error("Nomor polisi wajib diisi");
    if (!form.damage_description.trim()) return toast.error("Deskripsi kerusakan wajib diisi");
    setSaving(true);
    if (targetWorkshop === "__other__" && !manualWorkshop.trim())
      return toast.error("Isi nama bengkel lain");
    const payload = {
      ...form,
      damage_files: files.map((f) => f.id),
      preferred_workshop_id:
        targetWorkshop && targetWorkshop !== "__other__" ? targetWorkshop : null,
      workshop_name_manual: targetWorkshop === "__other__" ? manualWorkshop.trim() : null,
      submit: doSubmit,
    };
    try {
      if (editing) {
        await api.put(`/reports/${id}`, payload);
      } else {
        await api.post("/reports", payload);
      }
      toast.success(doSubmit ? "Laporan diajukan untuk persetujuan" : "Draft tersimpan");
      navigate("/reports");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" /> Kembali
      </button>
      <div>
        <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900 md:text-5xl">
          {editing ? "Revisi Laporan" : "Lapor Kerusakan Kendaraan"}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Lengkapi data kerusakan. Anda dapat menyimpan sebagai draft atau langsung mengajukan.
        </p>
      </div>

      <div className="space-y-6 rounded-md border border-slate-200 bg-white p-6 shadow-sm">
        {/* Vehicle */}
        <section className="space-y-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Data Kendaraan
          </p>
          {vehicles.length > 0 && (
            <div className="space-y-1.5">
              <Label>Pilih dari Master Data (opsional)</Label>
              <Select onValueChange={pickVehicle}>
                <SelectTrigger data-testid="vehicle-select">
                  <SelectValue placeholder="Pilih kendaraan…" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.plate_number}>
                      {v.plate_number} — {v.brand} {v.type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Nomor Polisi *</Label>
              <Input
                data-testid="input-plate"
                placeholder="B 1234 XYZ"
                value={form.plate_number}
                onChange={(e) => set("plate_number", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Jenis / Tipe</Label>
              <Input
                data-testid="input-type"
                placeholder="Minibus / Pickup"
                value={form.vehicle_type}
                onChange={(e) => set("vehicle_type", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Merek</Label>
              <Input
                data-testid="input-brand"
                placeholder="Toyota"
                value={form.brand}
                onChange={(e) => set("brand", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Tahun</Label>
              <Input
                data-testid="input-year"
                placeholder="2020"
                value={form.year}
                onChange={(e) => set("year", e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* Reporter & incident */}
        <section className="space-y-4 border-t border-slate-100 pt-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Data Pelapor & Kejadian
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Nama Pelapor</Label>
              <Input value={user?.name || ""} disabled />
            </div>
            <div className="space-y-1.5">
              <Label>Divisi</Label>
              <Input value={user?.division_name || "-"} disabled />
            </div>
            <div className="space-y-1.5">
              <Label>No. Telepon</Label>
              <Input
                data-testid="input-phone"
                placeholder="08xxxxxxxxxx"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Tanggal Kejadian</Label>
              <DatePicker
                testId="input-date"
                value={form.incident_date}
                onChange={(v) => set("incident_date", v)}
                placeholder="Pilih tanggal kejadian"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Lokasi Kejadian</Label>
              <Input
                data-testid="input-location"
                placeholder="Jl. Merdeka No.1, Jakarta"
                value={form.location}
                onChange={(e) => set("location", e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* Damage */}
        <section className="space-y-4 border-t border-slate-100 pt-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Detail Kerusakan
          </p>
          <div className="space-y-1.5">
            <Label>Deskripsi Kerusakan *</Label>
            <Textarea
              data-testid="input-description"
              rows={4}
              placeholder="Jelaskan kerusakan yang terjadi…"
              value={form.damage_description}
              onChange={(e) => set("damage_description", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Tingkat Urgensi</Label>
            <Select value={form.urgency} onValueChange={(v) => set("urgency", v)}>
              <SelectTrigger data-testid="input-urgency" className="w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {URGENCY.map((u) => (
                  <SelectItem key={u.v} value={u.v}>
                    {u.l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Dokumentasi Kerusakan (foto / video)</Label>
            <FileUploader files={files} setFiles={setFiles} testId="damage-uploader" />
            <p className="text-xs text-slate-400">
              Bisa unggah beberapa file sekaligus. Cocok difoto langsung dari HP di lokasi.
            </p>
          </div>
        </section>

        <section className="space-y-4 border-t border-slate-100 pt-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Bengkel Tujuan (Usulan)
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Pilih Bengkel</Label>
              <Select value={targetWorkshop} onValueChange={setTargetWorkshop}>
                <SelectTrigger data-testid="target-workshop-select">
                  <SelectValue placeholder="Pilih bengkel…" />
                </SelectTrigger>
                <SelectContent>
                  {workshops.map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="__other__">Bengkel Lain (ketik manual)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {targetWorkshop === "__other__" && (
              <div className="space-y-1.5">
                <Label>Nama Bengkel Lain</Label>
                <Input
                  data-testid="manual-workshop-input"
                  placeholder="mis. Bengkel Resmi Daihatsu Cibubur"
                  value={manualWorkshop}
                  onChange={(e) => setManualWorkshop(e.target.value)}
                />
              </div>
            )}
          </div>
          <p className="text-xs text-slate-400">
            Jika kendaraan tidak bisa diservis di bengkel yang tersedia, pilih "Bengkel Lain" dan
            ketik namanya. Approver akan mengonfirmasi bengkel tujuan saat menyetujui.
          </p>
        </section>

        <div className="flex flex-wrap gap-3 border-t border-slate-100 pt-6">
          <Button
            data-testid="submit-report-btn"
            className="bg-[var(--sipkd-primary)] hover:bg-[var(--sipkd-primary-hover)]"
            disabled={saving}
            onClick={() => submit(true)}
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Ajukan Persetujuan
          </Button>
          <Button
            data-testid="save-draft-btn"
            variant="outline"
            disabled={saving}
            onClick={() => submit(false)}
          >
            <Save className="mr-2 h-4 w-4" /> Simpan Draft
          </Button>
        </div>
      </div>
    </div>
  );
}
